import { useCacheHelpers } from '../apollo/cache-helpers'
import { useBoreholeFieldContainers } from '../borehole-field-container/use-borehole-field-containers'
import {
  BoreholeFieldContainerInput,
  BuildingFragment,
  BuildingFragmentDoc,
  CreateBuildingInput,
  ProjectVersionFragment,
  UpdateBuildingInput,
  useCreateBuildingMutation,
  useDeleteBuildingMutation,
  useUpdateBuildingBoreholeFieldMutation,
  useUpdateBuildingMutation,
} from '../generated/graphql'

type BuildingMutations = {
  isCreateLoading: boolean
  isUpdateLoading: boolean
  isUpdateBoreholeFieldLoading: boolean
  isDeleteLoading: boolean

  createBuilding: (
    projectVersion: ProjectVersionFragment,
    input: CreateBuildingInput,
  ) => Promise<BuildingFragment>

  updateBuilding: (
    id: string,
    input: UpdateBuildingInput,
  ) => Promise<BuildingFragment>

  updateBuildingBoreholeField: (
    id: string,
    input: BoreholeFieldContainerInput,
  ) => Promise<BuildingFragment>

  deleteBuilding: (
    projectVersion: ProjectVersionFragment,
    id: string,
  ) => Promise<boolean>
}

export function useBuildings(): BuildingMutations {
  const [createBuilding, { loading: isCreateLoading }] =
    useCreateBuildingMutation()
  const [updateBuilding, { loading: isUpdateLoading }] =
    useUpdateBuildingMutation()
  const [
    updateBuildingBoreholeField,
    { loading: isUpdateBoreholeFieldLoading },
  ] = useUpdateBuildingBoreholeFieldMutation()
  const [deleteBuilding, { loading: isDeleteLoading }] =
    useDeleteBuildingMutation()
  const { addItemToList, removeItemFromList } = useCacheHelpers()
  const { getContainerForUpdateOptimisticResponse } =
    useBoreholeFieldContainers()

  return {
    isCreateLoading,
    isUpdateLoading,
    isDeleteLoading,
    isUpdateBoreholeFieldLoading,

    async createBuilding(
      projectVersion: ProjectVersionFragment,
      input: CreateBuildingInput,
    ) {
      const result = await createBuilding({
        variables: {
          projectVersionId: projectVersion.id,
          input,
        },

        update: (_, mutationResult) => {
          const building = mutationResult.data?.createBuilding
          if (!building) {
            return
          }

          addItemToList(
            BuildingFragmentDoc,
            building,
            projectVersion,
            'buildings',
          )
        },
      })

      if (!result.data) {
        throw new Error('Building creation failed')
      }

      return result.data.createBuilding
    },

    async updateBuilding(id: string, input: UpdateBuildingInput) {
      const result = await updateBuilding({
        variables: { id, input },
      })

      if (!result.data) {
        throw new Error('Building update failed')
      }

      return result.data.updateBuilding
    },

    async updateBuildingBoreholeField(
      id: string,
      input: BoreholeFieldContainerInput,
    ) {
      const result = await updateBuildingBoreholeField({
        variables: { id, input },

        optimisticResponse: () => {
          const buildingFragment =
            getContainerForUpdateOptimisticResponse<BuildingFragment>(
              id,
              input,
              BuildingFragmentDoc,
            )

          return { updateBuildingBoreholeField: buildingFragment }
        },
      })

      if (!result.data) {
        throw new Error('Building borehole field update failed')
      }

      return result.data.updateBuildingBoreholeField
    },

    async deleteBuilding(projectVersion: ProjectVersionFragment, id: string) {
      const result = await deleteBuilding({
        variables: { id },

        update: () => {
          removeItemFromList(
            BuildingFragmentDoc,
            id,
            projectVersion,
            'buildings',
          )
        },

        optimisticResponse: {
          deleteBuilding: true,
        },
      })

      if (!result.data) {
        throw new Error('Building deletion failed')
      }

      return result.data.deleteBuilding
    },
  }
}
